import React from 'react';
import { AlertTriangle, CheckCircle2, Clock, Info, XCircle } from 'lucide-react';
import { cn } from './cn';

/**
 * Sistema de status do Atmos.
 *
 * Regra que o design system trata como inegociável: a cor NUNCA carrega o
 * significado sozinha. Todo status sai com ícone + rótulo, para continuar
 * legível no daltonismo e em relatório impresso em preto e branco.
 */
export type Tone = 'neutral' | 'primary' | 'success' | 'warning' | 'danger' | 'info';

const TONE: Record<Tone, string> = {
  neutral: 'bg-neutral-soft border-neutral-line text-fg-2',
  primary: 'bg-primary-soft border-primary-line text-primary',
  success: 'bg-success-soft border-success-line text-success',
  warning: 'bg-warning-soft border-warning-line text-warning',
  danger: 'bg-danger-soft border-danger-line text-danger',
  info: 'bg-info-soft border-info-line text-info',
};

export interface BadgeProps {
  tone?: Tone;
  icon?: React.ReactNode;
  className?: string;
  children: React.ReactNode;
}

export function Badge({ tone = 'neutral', icon, className, children }: BadgeProps) {
  return (
    <span
      className={cn(
        'inline-flex h-6 items-center gap-1.5 whitespace-nowrap rounded-md border px-[9px] text-xs font-medium',
        TONE[tone],
        className,
      )}
    >
      {icon}
      {children}
    </span>
  );
}

/** Ícone padrão de cada tom, para quem não quiser escolher um. */
const TONE_ICON: Record<Tone, React.ReactNode> = {
  neutral: <Clock size={13} className="flex-none" />,
  primary: <Info size={13} className="flex-none" />,
  success: <CheckCircle2 size={13} className="flex-none" />,
  warning: <AlertTriangle size={13} className="flex-none" />,
  danger: <XCircle size={13} className="flex-none" />,
  info: <Info size={13} className="flex-none" />,
};

/** Badge de status com o ícone do tom já aplicado. */
export function StatusBadge({
  tone = 'neutral',
  icon,
  className,
  children,
}: BadgeProps) {
  return (
    <Badge tone={tone} icon={icon ?? TONE_ICON[tone]} className={className}>
      {children}
    </Badge>
  );
}

/** Contador discreto — usado no menu e em abas. */
export function Counter({ children, className }: { children: React.ReactNode; className?: string }) {
  return (
    <span
      className={cn(
        'grid h-[18px] min-w-5 place-items-center rounded-full bg-neutral-soft px-1.5 text-[11px] font-semibold tabular-nums text-fg-2',
        className,
      )}
    >
      {children}
    </span>
  );
}
